import type { ChatToolCall } from "./types";

const READ_TOOLS = new Set(["list_files", "read_file"]);

/**
 * One progress line in the artifact card. Replit-style:
 *   ● Writing app/(tabs)/index.tsx
 *   ◐ Adding dependency expo-linear-gradient
 *   ✓ Reading package.json
 */
export function ToolCallRow({ call }: { call: ChatToolCall }) {
  const isRead = READ_TOOLS.has(call.name);
  const running = call.result === undefined && call.ok === undefined;
  const failed = call.ok === false;

  const symbol = failed ? "✗" : running ? "◐" : isRead ? "○" : "●";
  const color = failed
    ? "var(--accent-red)"
    : running
      ? "var(--accent-blue)"
      : isRead
        ? "var(--text-tertiary)"
        : "var(--accent-green)";

  const label = friendlyLabel(call);

  return (
    <li
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: 8,
        fontSize: 11.5,
        lineHeight: 1.45,
        color: "var(--text-secondary)",
      }}
    >
      <span
        style={{
          color,
          fontFamily: "var(--font-mono)",
          fontSize: 10,
          lineHeight: 1.6,
          flexShrink: 0,
          width: 12,
          textAlign: "center",
        }}
      >
        {symbol}
      </span>
      <span style={{ flex: 1, minWidth: 0, overflowWrap: "anywhere" }}>{label}</span>
    </li>
  );
}

function friendlyLabel(call: ChatToolCall): string {
  const args = (call.args as Record<string, unknown> | undefined) ?? undefined;
  const arg = (k: string) => (args && typeof args[k] === "string" ? (args[k] as string) : undefined);

  switch (call.name) {
    case "write_file":
      return `Writing ${arg("path") ?? "file"}`;
    case "edit_file":
      return `Editing ${arg("path") ?? "file"}`;
    case "delete_file":
      return `Deleting ${arg("path") ?? "file"}`;
    case "read_file":
      return `Reading ${arg("path") ?? "file"}`;
    case "list_files":
      return `Listing ${arg("path") ?? "/"}`;
    case "add_dependency": {
      const pkg = arg("package") ?? arg("name") ?? "package";
      return `Adding dependency ${pkg}`;
    }
    case "plan":
      return arg("title") ?? "Planning";
    default:
      return call.name.replace(/_/g, " ");
  }
}
